import React from 'react';
import { IconType } from 'react-icons';
import ChakraIcon from '../components/common/ChakraIcon';

import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Text,
  Stack,
  SimpleGrid,
  Image,
  Icon,
  useBreakpointValue,
  VStack,
  HStack,
  Divider,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { FaHandHoldingHeart, FaBookReader, FaUsers, FaHandsHelping, FaSeedling, FaHeartbeat } from 'react-icons/fa';

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionText = motion(Text);
const MotionHeading = motion(Heading);

const Home: React.FC = () => {
  // Responsive text sizes
  const heroHeadingSize = useBreakpointValue({ base: '3xl', md: '4xl', lg: '5xl' });
  const heroSubheadingSize = useBreakpointValue({ base: 'md', md: 'lg' });
  
  // Animation variants with proper typing
  const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.8 } }
  };
  
  const slideUp = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };
  
  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Define interface for activity data
  interface Activity {
    title: string;
    description: string;
    icon: IconType;
    color: string;
  }

  // Activity cards data
  const activities: Activity[] = [
    {
      title: 'Education & Training',
      description: 'Providing knowledge and skills to empower individuals and communities.',
      icon: FaBookReader,
      color: 'brand.500'
    },
    {
      title: 'Advocacy',
      description: 'Amplifying voices and advocating for policies that promote peace and justice.',
      icon: FaUsers,
      color: 'green.500'
    },
    {
      title: 'Community Initiatives',
      description: 'Supporting local projects that address specific community needs.',
      icon: FaHandsHelping,
      color: 'earth.500'
    },
    {
      title: 'Partnerships',
      description: 'Collaborating with organizations to maximize impact and reach.',
      icon: FaHandHoldingHeart,
      color: 'brand.500'
    },
    {
      title: 'Resource Provision',
      description: 'Ensuring communities have access to essential resources for development.',
      icon: FaSeedling,
      color: 'green.500'
    },
    {
      title: 'Mental & Emotional Wellbeing',
      description: 'Supporting holistic health through counseling and wellness programs.',
      icon: FaHeartbeat,
      color: 'earth.500'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        position="relative"
        h={{ base: '90vh', md: '100vh' }}
        bgImage="linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('/assets/images/photo_2025-05-04_15-28-27.jpg')"
        bgPosition="center"
        bgSize="cover"
        color="white"
      >
        <Container maxW="container.xl" h="100%">
          <Flex
            direction="column"
            align="center"
            justify="center"
            textAlign="center"
            h="100%"
            pt={{ base: '60px', md: '0' }}
          >
            <MotionHeading
              as={motion.h1}
              fontSize={heroHeadingSize}
              fontWeight="bold"
              mb={4}
              initial="hidden"
              animate="visible"
              variants={fadeIn}
            >
              (ALPHA) ACTION FOR LIFE PEACE AND <br />
              HOPE ADVANCEMENT
            </MotionHeading>
            
            <MotionText
              fontSize={heroSubheadingSize}
              maxW="800px"
              mb={8}
              initial="hidden"
              animate="visible"
              variants={slideUp}
            >
              Empowering communities in the Democratic Republic of Congo through collaborative initiatives, education, and advocacy.
            </MotionText>
            
            <MotionBox
              initial="hidden"
              animate="visible"
              variants={slideUp}
            >
              <Stack
                direction={{ base: 'column', sm: 'row' }}
                spacing={4}
                align="center"
              >
                <Button
                  as={RouterLink}
                  to="/get-involved"
                  variant="primary"
                  size="lg"
                  px={8}
                >
                  Get Involved
                </Button>
                <Button
                  as={RouterLink}
                  to="/about"
                  variant="outline"
                  size="lg"
                  px={8}
                  bg="transparent"
                  color="white"
                  borderColor="white"
                  _hover={{ bg: 'whiteAlpha.200' }}
                >
                  Learn More
                </Button>
              </Stack>
            </MotionBox>
          </Flex>
        </Container>
      </Box>

      {/* Mission Section */}
      <Box py={{ base: 12, md: 20 }} bg="gray.50">
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} alignItems="center">
            <Box>
              <Image
                src="/assets/images/photo_2025-05-04_15-29-33.jpg"
                alt="ALPHA Mission"
                borderRadius="lg"
                boxShadow="lg"
                w="full"
                h="auto"
              />
            </Box>
            <MotionFlex
              direction={{ base: 'column', lg: 'row' }}
              align="center"
              justify="space-between"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={staggerChildren}
            >
              <MotionBox
                flex="1"
                mr={{ base: 0, lg: 10 }}
                mb={{ base: 10, lg: 0 }}
                variants={slideUp}
              >
                <Image
                  src="/assets/images/photo_2025-05-04_15-30-06.jpg"
                  alt="ALPHA Mission"
                  borderRadius="lg"
                  boxShadow="lg"
                  w="full"
                  h="auto"
                />
              </MotionBox>
              <MotionBox flex="1" variants={slideUp}>
                <Heading
                  as="h2"
                  fontSize={{ base: '2xl', md: '3xl' }}
                  mb={4}
                  color="brand.500"
                >
                  Our Mission
                </Heading>
                
                <Divider w="100px" borderColor="brand.500" borderWidth="2px" mb={6} />
                
                <Text fontSize={{ base: 'md', md: 'lg' }} mb={6}>
                  To empower individuals and communities to thrive by promoting the preservation of life, fostering sustainable peace, cultivating hope, and advancing holistic well-being through collaborative initiatives, education, and advocacy.
                </Text>
                
                <Text fontSize={{ base: 'md', md: 'lg' }} mb={6}>
                  We believe in a world where all individuals and communities experience safety, peace, and happiness, with equitable access to resources and opportunities for a fulfilling life.
                </Text>
                
                <Button
                  as={RouterLink}
                  to="/about"
                  variant="primary"
                  size="md"
                  mt={4}
                >
                  About Us
                </Button>
              </MotionBox>
            </MotionFlex>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Key Activities Section */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <MotionBox
            textAlign="center"
            mb={16}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <Heading
              as="h2"
              fontSize={{ base: '2xl', md: '3xl' }}
              mb={4}
            >
              Our Key Activities
            </Heading>
            
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              maxW="800px"
              mx="auto"
            >
              Through Education, Dialogue, and Advocacy (EDA), we work to transform communities and create lasting positive change.
            </Text>
          </MotionBox>
          
          <MotionFlex
            as={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={staggerChildren}
          >
            <SimpleGrid
              columns={{ base: 1, md: 2, lg: 3 }}
              spacing={10}
              w="full"
            >
              {activities.map((activity, index) => (
                <MotionBox
                  key={index}
                  p={6}
                  borderRadius="lg"
                  boxShadow="md"
                  bg="white"
                  transition={{ duration: 0.3, ease: "ease" }}
                  _hover={{ transform: 'translateY(-5px)', boxShadow: 'lg' }}
                  variants={slideUp}
                >
                  <Flex direction="column" align="center" textAlign="center">
                    <Flex
                      w="70px"
                      h="70px"
                      borderRadius="full"
                      bg={`${activity.color}10`}
                      color={activity.color}
                      justify="center"
                      align="center"
                      mb={4}
                    >
                      <ChakraIcon icon={activity.icon} boxSize="32px" />
                    </Flex>
                    
                    <Heading as="h3" fontSize="xl" mb={3}>
                      {activity.title}
                    </Heading>
                    
                    <Text color="gray.600">
                      {activity.description}
                    </Text>
                  </Flex>
                </MotionBox>
              ))}
            </SimpleGrid>
          </MotionFlex>
          
          <Flex justify="center" mt={12}>
            <Button
              as={RouterLink}
              to="/programs"
              variant="primary"
              size="lg"
            >
              Explore Our Programs
            </Button>
          </Flex>
        </Container>
      </Box>

      {/* Impact Section */}
      <Box py={{ base: 12, md: 20 }} bg="brand.500" color="white">
        <Container maxW="container.xl">
          <MotionBox
            textAlign="center"
            mb={16}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <Heading
              as="h2"
              fontSize={{ base: '2xl', md: '3xl' }}
              mb={4}
            >
              Our Impact
            </Heading>
            
            <Text
              fontSize={{ base: 'md', md: 'lg' }}
              maxW="800px"
              mx="auto"
            >
              We measure our success through improved access to resources, reduced violence, increased well-being, and strengthened community resilience.
            </Text>
          </MotionBox>
          
          <MotionFlex
            as={motion.div}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
            align="center"
            textAlign="center"
          >
            <MotionBox variants={slideUp} p={6}>
              <Heading as="h3" fontSize={{ base: '4xl', md: '6xl' }}>500+</Heading>
              <Text fontSize="lg">Community Members Trained</Text>
            </MotionBox>
            
            <MotionBox variants={slideUp} p={6}>
              <Heading as="h3" fontSize={{ base: '4xl', md: '6xl' }}>10+</Heading>
              <Text fontSize="lg">Community Projects</Text>
            </MotionBox>
            
            <MotionBox variants={slideUp} p={6}>
              <Heading as="h3" fontSize={{ base: '4xl', md: '6xl' }}>5+</Heading>
              <Text fontSize="lg">Partner Organizations</Text>
            </MotionBox>
            
            <MotionBox variants={slideUp} p={6}>
              <Heading as="h3" fontSize={{ base: '4xl', md: '6xl' }}>3</Heading>
              <Text fontSize="lg">Regions Served</Text>
            </MotionBox>
          </MotionFlex>
          
          <Flex justify="center" mt={12}>
            <Button
              as={RouterLink}
              to="/impact-stories"
              variant="outline"
              size="lg"
              color="white"
              borderColor="white"
              _hover={{ bg: 'whiteAlpha.200' }}
            >
              Read Impact Stories
            </Button>
          </Flex>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <MotionFlex
            direction={{ base: 'column', md: 'row' }}
            align="center"
            justify="space-between"
            bg="gray.50"
            p={{ base: 8, md: 12 }}
            borderRadius="lg"
            boxShadow="md"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            <MotionBox
              flex="1"
              mr={{ base: 0, md: 8 }}
              mb={{ base: 6, md: 0 }}
              variants={slideUp}
            >
              <Heading
                as="h2"
                fontSize={{ base: '2xl', md: '3xl' }}
                mb={4}
              >
                Join Us in Making a Difference
              </Heading>
              
              <Text fontSize={{ base: 'md', md: 'lg' }} mb={4}>
                Together, we can create a world where all individuals and communities thrive through the advancement of life, peace, hope, and holistic happiness.
              </Text>
            </MotionBox>
            
            <MotionBox variants={slideUp}>
              <Stack
                direction={{ base: 'column', sm: 'row' }}
                spacing={4}
              >
                <Button
                  as={RouterLink}
                  to="/get-involved"
                  variant="primary"
                  size="lg"
                >
                  Donate Now
                </Button>
                <Button
                  as={RouterLink}
                  to="/contact"
                  variant="outline"
                  colorScheme="blue"
                  size="lg"
                >
                  Contact Us
                </Button>
              </Stack>
            </MotionBox>
          </MotionFlex>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
